/**
 * Google Cloud Storage is the upload data plane, not a Vision Lab JSON API. Keeping it in the
 * transfer feature still gives callers one place for signed/resumable PUT semantics without adding
 * bearer tokens or the application's `/api` prefix to capability URLs.
 */

export class GoogleStorageUploadNetworkError extends Error {
  readonly transport = 'gcs_resumable_xhr';

  constructor(
    readonly xhrStatus: number,
    readonly xhrReadyState: number,
    readonly browserOnline: boolean,
    readonly chunkStartBytes: number,
    readonly chunkSizeBytes: number,
    readonly finalChunk: boolean,
  ) {
    super('network error');
    this.name = 'GoogleStorageUploadNetworkError';
  }
}

export interface PutGoogleStorageChunkInput {
  headers?: Record<string, string>;
  uploadUrl: string;
  chunk: Blob;
  start: number;
  totalBytes: number;
  finalChunk: boolean;
  onProgress: (loadedBytes: number) => void;
  onRequest: (request: XMLHttpRequest) => void;
  onSettled: (request: XMLHttpRequest) => void;
}

/** PUT one byte range to a backend-issued GCS resumable capability URL. */
export function putGoogleStorageChunk({
  headers,
  uploadUrl,
  chunk,
  start,
  totalBytes,
  finalChunk,
  onProgress,
  onRequest,
  onSettled,
}: PutGoogleStorageChunkInput): Promise<number> {
  return new Promise((resolve, reject) => {
    const request = new XMLHttpRequest();
    request.open('PUT', uploadUrl, true);
    for (const [name, value] of Object.entries(headers ?? {}))
      request.setRequestHeader(name, value);
    request.setRequestHeader(
      'Content-Range',
      `bytes ${start}-${start + chunk.size - 1}/${totalBytes}`,
    );
    request.upload.onprogress = (event) => onProgress(event.loaded);
    request.onload = () => {
      onSettled(request);
      resolve(request.status);
    };
    request.onerror = () => {
      onSettled(request);
      reject(
        new GoogleStorageUploadNetworkError(
          request.status,
          request.readyState,
          navigator.onLine,
          start,
          chunk.size,
          finalChunk,
        ),
      );
    };
    request.onabort = () => {
      onSettled(request);
      reject(new DOMException('aborted', 'AbortError'));
    };
    onRequest(request);
    request.send(chunk);
  });
}

/** PUT one PDQ JPEG to a backend-issued signed object URL. */
export async function putGoogleStorageSignedFrame(uploadUrl: string, blob: Blob): Promise<void> {
  const response = await fetch(uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'image/jpeg' },
    body: blob,
  });
  if (!response.ok) throw new Error(`Signed frame PUT failed with status ${response.status}`);
}
